import React from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaEdit, FaEnvelope, FaMapMarkerAlt, FaPhone, FaShieldAlt, FaWallet } from "react-icons/fa";
import adminApi from "services/admin-api";
import { formatDate, formatMoney } from "utils/admin";
import { canAccessPortalPath } from "utils/portal-access";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { PageHeader, SectionCard, StatCard, StatusPill } from "components/admin/ui";
import { useAuth } from "context/auth-context";
import {
  ProfileContactIcon,
  ProfileContactItem,
  ProfileContactRow,
  ProfileHeroContent,
  ProfileHeroLayout,
  ProfileHeroMeta,
  ProfileHeroName,
  ProfileHeroNameRow,
  ProfilePreviewImage,
  ProfilePrimaryButton,
  ProfileSecondaryButton,
  ProfileOptionButton,
  ProfileOptionRow,
  ProfileSection,
  ProfileStatsGrid,
  ProfileWalletLabel,
  ProfileWalletMeta,
  ProfileWalletRow,
  ProfileWalletStatus,
  ProfileWorkspaceCard,
  ProfileWorkspaceHeader,
  ProfileWorkspaceSubtitle,
  ProfileWorkspaceTitle,
} from "pages/app/users/styles/profile.page.inline.styles";

const buildLocation = (user?: {
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
}) =>
  [user?.street_address, user?.city, user?.state, user?.zip_code, user?.country]
    .filter(Boolean)
    .join(", ") || "Not set";

export const ProfilePage = () => {
  const history = useHistory();
  const { schoolThemePreference, setSchoolThemePreference } = useAuth();
  const { data: user } = useQuery({
    queryKey: ["profile"],
    queryFn: adminApi.currentUser,
  });
  const canAccessWallet = canAccessPortalPath("/workspace/wallet", user?.portal_access);
  const { data: wallet, isError: isWalletError, isLoading: isWalletLoading } = useQuery({
    queryKey: ["workspace-wallet"],
    queryFn: adminApi.wallets.me,
    enabled: Boolean(user) && canAccessWallet,
  });
  const displayName = user?.full_name || user?.name || "Unknown user";
  const walletBalance = wallet?.formatted_balance || formatMoney(wallet?.balance);
  const walletStatus = isWalletLoading
    ? "Loading wallet..."
    : isWalletError
    ? "Wallet unavailable"
    : wallet
    ? `${walletBalance} available`
    : "No wallet available";

  return (
    <AppLayout route="/profile">
      <PageHeader
        title="My profile"
        description="Account, access and workspace settings"
        actions={
          <>
            <ProfileSecondaryButton
              onClick={() => window.dispatchEvent(new Event("athspire:open-sessions"))}
              type="button"
            >
              <FaShieldAlt size={12} />
              Signed-in devices
            </ProfileSecondaryButton>
            <ProfilePrimaryButton onClick={() => history.push("/profile/edit")} type="button">
              <FaEdit size={12} />
              Edit profile
            </ProfilePrimaryButton>
          </>
        }
      />

      <ProfileSection>
        <SectionCard>
          <ProfileHeroLayout>
            <ProfilePreviewImage
              alt={displayName}
              fallbackLabel={displayName || user?.email}
              src={user?.image}
              variant="user"
            />
            <ProfileHeroContent>
              <ProfileHeroNameRow>
                <ProfileHeroName>{displayName}</ProfileHeroName>
                <StatusPill value={user?.role} />
              </ProfileHeroNameRow>
              <ProfileHeroMeta>Member since {formatDate(user?.created_at)}</ProfileHeroMeta>
              <ProfileContactRow>
                <ProfileContactItem>
                  <ProfileContactIcon>
                    <FaEnvelope size={14} />
                  </ProfileContactIcon>
                  <span>{user?.email || "Not set"}</span>
                </ProfileContactItem>
                <ProfileContactItem>
                  <ProfileContactIcon>
                    <FaPhone size={14} />
                  </ProfileContactIcon>
                  <span>{user?.phone_number || "Not set"}</span>
                </ProfileContactItem>
                <ProfileContactItem>
                  <ProfileContactIcon>
                    <FaMapMarkerAlt size={14} />
                  </ProfileContactIcon>
                  <span>{buildLocation(user)}</span>
                </ProfileContactItem>
              </ProfileContactRow>
            </ProfileHeroContent>
          </ProfileHeroLayout>

        </SectionCard>
      </ProfileSection>

      <ProfileStatsGrid>
        <StatCard label="Orders" value={user?.orders?.length ?? 0} />
        <StatCard label="Payments" value={user?.payments?.length ?? 0} />
      </ProfileStatsGrid>

      {canAccessWallet ? (
        <ProfileSection>
          <SectionCard>
            <ProfileWalletRow>
              <ProfileWalletLabel>
                <FaWallet size={16} />
                Wallet
              </ProfileWalletLabel>
              <ProfileWalletMeta>
                <ProfileWalletStatus>{walletStatus}</ProfileWalletStatus>
                {canAccessWallet && !isWalletError ? (
                  <ProfileSecondaryButton
                    onClick={() => history.push("/workspace/wallet")}
                    type="button"
                  >
                    Open wallet
                  </ProfileSecondaryButton>
                ) : null}
              </ProfileWalletMeta>
            </ProfileWalletRow>
          </SectionCard>
        </ProfileSection>
      ) : null}

      <ProfileSection>
        <ProfileWorkspaceCard>
          <ProfileWorkspaceHeader>
            <div>
              <ProfileWorkspaceTitle>Workspace appearance</ProfileWorkspaceTitle>
              <ProfileWorkspaceSubtitle>
                Follow school branding or keep the classic Athspire look
              </ProfileWorkspaceSubtitle>
            </div>
            <ProfileOptionRow>
              {[
                { label: "School branding", value: "school_brand" as const },
                { label: "Classic", value: "classic" as const },
              ].map((option) => {
                const isActive = schoolThemePreference === option.value;
                return (
                  <ProfileOptionButton
                    $isActive={isActive}
                    aria-pressed={isActive}
                    key={option.value}
                    onClick={() => setSchoolThemePreference(option.value)}
                  >
                    {option.label}
                  </ProfileOptionButton>
                );
              })}
            </ProfileOptionRow>
          </ProfileWorkspaceHeader>
        </ProfileWorkspaceCard>
      </ProfileSection>
    </AppLayout>
  );
};
export default ProfilePage;
